// @flow

import React, { type Node } from 'react';
import { Query } from 'react-apollo';
import { compose, mapProps, withStateHandlers } from 'recompose';
import gql from 'graphql-tag';
import pbx from '@catalytic/pushbot-expression';

import { toExpressionScope } from './ExpressionTransform';
import {
  type ExpressionScope,
  type ExpressionScopeFields
} from './ExpressionTypes';
import { withIsEmptyConditions } from '../Feature';
import logError from '../utils/logError';

const DEFAULT_CONDITION = true;

export const Context = gql`
  query Context($context: String!) {
    context(id: $context) {
      id
      owner {
        id
      }
      tasks {
        nodes {
          id
          name
          status
        }
      }
      fields {
        nodes {
          id
          name
          value
        }
      }
    }
  }
`;

export type Evaluate = (condition: string) => boolean;
export type Update = (name: string, value: any) => void;

export type Props = {
  children: ({
    evaluate: Evaluate,
    loading: boolean,
    update: Update
  }) => Node,
  context: string,
  evaluate: Evaluate,
  fetchPolicy?: string,
  fields: ExpressionScopeFields,
  isEmptyConditionsLoading?: boolean,
  loading: boolean,
  query?: Object,
  transform?: (data: Object) => ExpressionScope,
  update: Update,
  useCorrectIsEmpty: boolean
};

export const isValidCondition = (condition?: string): boolean => {
  return typeof condition === 'string' && condition !== '';
};

// While `useCorrectIsEmpty` is still resolving (`isLoading`), it holds a
// provisional value, not the server-confirmed one -- see Variation.js. A
// caller that can't tell the difference (like TaskWebform.js clearing a
// field when a condition evaluates to false) could act on the wrong value
// for the split second before it resolves. Evaluating every condition as
// met until then is the same safe fallback already used for invalid
// conditions and eval errors below.
export const createEvaluate = (
  scope: ExpressionScope,
  useCorrectIsEmpty: boolean,
  isLoading?: boolean
): Evaluate => (condition?: string) => {
  if (isLoading) {
    return DEFAULT_CONDITION;
  }

  if (isValidCondition(condition)) {
    try {
      return pbx.eval(condition, scope, { useCorrectIsEmpty });
    } catch (error) {
      console.error(error);

      return DEFAULT_CONDITION;
    }
  } else {
    return DEFAULT_CONDITION;
  }
};

export const mergeScopeWithState = (
  scope: ExpressionScope,
  fields: ExpressionScopeFields
): ExpressionScope => ({
  ...scope,
  fields: {
    ...scope.fields,
    ...fields
  }
});

const ExpressionContext = ({
  children,
  context,
  fetchPolicy,
  fields,
  isEmptyConditionsLoading,
  query,
  transform,
  update,
  useCorrectIsEmpty
}: Props) => {
  return (
    <Query
      errorPolicy="all"
      fetchPolicy={fetchPolicy || 'network-only'}
      query={query || Context}
      variables={{ context }}
    >
      {({ data, error, loading }) => {
        logError(error);

        return children({
          contextLoading: loading && !data?.context,
          evaluate: createEvaluate(
            mergeScopeWithState(
              (transform || toExpressionScope)(data || {}),
              fields
            ),
            useCorrectIsEmpty,
            isEmptyConditionsLoading
          ),
          loading,
          update
        });
      }}
    </Query>
  );
};

ExpressionContext.displayName = 'ExpressionContext';

export default compose(
  withIsEmptyConditions,
  mapProps(({ isEmptyConditions, ...props }) => ({
    useCorrectIsEmpty: isEmptyConditions,
    ...props
  })),
  withStateHandlers(() => ({ fields: {} }), {
    update: ({ fields }) => (name: string, value: any) => ({
      fields: {
        ...fields,
        [name]: value
      }
    })
  })
)(ExpressionContext);
